import { useEffect } from 'react';
import Footer from '../home/components/Footer';

export default function CustomerTermsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-teal-500 to-teal-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-4xl font-bold mb-4">Customer Terms & Conditions</h1>
          <p className="text-teal-50 text-lg">Last updated: 15 January 2025</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-700 leading-relaxed mb-8">
            These Customer Terms & Conditions ("Terms") govern your access to and use of the Taskbudy platform as a customer ("Customer", "you", "your"). By registering, booking a service, or using the Platform, you agree to these Terms.
          </p>

          {/* Section 1 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. DEFINITIONS</h2>
            <ul className="space-y-2 text-gray-700">
              <li><strong>"Taskbudy" / "we" / "us"</strong> means Taskbudy Ltd.</li>
              <li><strong>"Platform"</strong> means the Taskbudy website, mobile application, and related services.</li>
              <li><strong>"Customer"</strong> means a user requesting services through the Platform.</li>
              <li><strong>"Provider"</strong> means an independent, self-employed service provider offering services via the Platform.</li>
              <li><strong>"Job"</strong> means a task requested by a Customer and accepted by a Provider.</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. ROLE OF TASKBUDY</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>2.1</strong> Taskbudy operates as a technology platform connecting Customers with independent service providers.</p>
              <p><strong>2.2</strong> Taskbudy does not provide the services itself and is not a party to the contract between Customer and Provider.</p>
              <p><strong>2.3</strong> Providers are not employees or agents of Taskbudy.</p>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. CUSTOMER ELIGIBILITY & ACCOUNT USE</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>3.1</strong> You must be at least 18 years old to use the Platform.</p>
              <p><strong>3.2</strong> You agree to provide accurate and up-to-date information.</p>
              <p><strong>3.3</strong> You are responsible for maintaining the confidentiality of your account details.</p>
            </div>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. REQUESTING & ACCEPTING JOBS</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>4.1</strong> Customers may request services through the Platform.</p>
              <p><strong>4.2</strong> Jobs are sent to available Providers based on location and category.</p>
              <p><strong>4.3</strong> A Job is formed when a Provider accepts your request.</p>
              <p><strong>4.4</strong> Taskbudy does not guarantee availability, response times, or completion of any Job.</p>
            </div>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. PRICING & PAYMENTS</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>5.1</strong> Prices for Jobs are shown or agreed through the Platform.</p>
              <p><strong>5.2</strong> Customers must pay in-app using the available payment methods.</p>
              <p><strong>5.3</strong> Payments are processed by third-party payment providers.</p>
              <p><strong>5.4</strong> Taskbudy may hold payments until the Job is completed or otherwise resolved.</p>
            </div>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. CANCELLATIONS & REFUNDS</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>6.1</strong> Cancellations and refunds are governed by the <a href="/cancellation-refund-policy" className="text-teal-600 hover:text-teal-700 underline">Cancellation & Refund Policy</a>.</p>
              <p><strong>6.2</strong> Refunds are not guaranteed and depend on the timing and circumstances of cancellation.</p>
              <p><strong>6.3</strong> Customers agree to be bound by decisions made in accordance with the Cancellation & Refund Policy.</p>
            </div>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. CUSTOMER OBLIGATIONS</h2>
            <p className="text-gray-700 mb-3">Customers agree to:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Provide accurate job details</li>
              <li>Ensure safe and reasonable access to the job location</li>
              <li>Treat Providers respectfully</li>
              <li>Not request or make payments outside the Platform</li>
            </ul>
          </section>

          {/* Section 8 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. PROVIDER PERFORMANCE & DISPUTES</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>8.1</strong> Providers are responsible for performing services with reasonable skill and care.</p>
              <p><strong>8.2</strong> If an issue arises, Customers should contact Taskbudy support through the Platform.</p>
              <p><strong>8.3</strong> Taskbudy may assist in dispute resolution but does not guarantee outcomes.</p>
            </div>
          </section>

          {/* Section 9 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. RATINGS & REVIEWS</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>9.1</strong> Customers may leave honest and fair reviews.</p>
              <p><strong>9.2</strong> Reviews must not be abusive, misleading, or unlawful.</p>
              <p><strong>9.3</strong> Taskbudy may remove reviews that breach these Terms.</p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. LIMITATION OF LIABILITY</h2>
            <p className="text-gray-700 mb-3">
              10.1 Taskbudy is not responsible for:
            </p>
            <ul className="list-disc pl-8 text-gray-700 mb-3 space-y-1">
              <li>The quality of services provided by Providers</li>
              <li>Acts or omissions of Providers or Customers</li>
            </ul>
            <p className="text-gray-700 mb-3">
              10.2 Nothing limits liability for death or personal injury caused by negligence or for fraud.
            </p>
            <p className="text-gray-700">
              10.3 To the extent permitted by law, Taskbudy's liability is limited to the amount paid by the Customer for the relevant Job.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. SUSPENSION & TERMINATION</h2>
            <p className="text-gray-700 mb-3">
              11.1 Taskbudy may suspend or terminate Customer accounts for misuse, abuse, fraud, or breach of these Terms.
            </p>
            <p className="text-gray-700">
              11.2 Customers may stop using the Platform at any time.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">12. DATA PROTECTION</h2>
            <p className="text-gray-700 mb-3">
              12.1 Personal data is processed in accordance with the Privacy Policy.
            </p>
            <p className="text-gray-700">
              12.2 Data may be shared with Providers and third parties where necessary to deliver services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">13. CHANGES TO TERMS</h2>
            <p className="text-gray-700 mb-3">
              13.1 Taskbudy may update these Terms from time to time.
            </p>
            <p className="text-gray-700">
              13.2 Continued use of the Platform constitutes acceptance of updated Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">14. GOVERNING LAW</h2>
            <p className="text-gray-700">
              These Terms are governed by the laws of England and Wales.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
